import React, { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";

import { MdCurrencyRupee } from "react-icons/md";
import { CALLER } from "../../../../core/data/constants/constant";
import useUpdateDeal from "../hooks/useUpdateDeal";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";
import {
  updateDealsListCache,
  updateSingleDealCache,
} from "../actions/updateDealsListCache";
import { generateQueryParams } from "../../../../utils/helpers/generateQueryParams";
import { getAllUrlParams } from "../../../../utils/helpers/setAndGetUrlParams";
import { useSelector } from "react-redux";
import { convertToUTCString } from "../../../../utils/helpers/helper";

const EditDealVerificationRecord = ({
  formik,
  userRole,
  isEditableByAccountant,
  isEditableByCaller,
  verificationRecordId,
  dealData,
}) => {
  const [editIndex, setEditIndex] = useState(
    formik?.values?.verificationRecord?.length > 0 ? 0 : null
  );
  const mutationUpdate = useUpdateDeal();
  const navigate = useNavigate();
  const urlParams = getAllUrlParams();

  const { filters } = useSelector((state) => state.filter);
  const dealsFilters = filters.deals || [];
  let { queryParams, params } = generateQueryParams(dealsFilters);

  useEffect(() => {
    if (editIndex !== null && editIndex !== -1) {
      handleEdit(editIndex);
    }
  }, [editIndex]);

  useEffect(() => {
    if (verificationRecordId) {
      const index = formik.values.verificationRecord.findIndex(
        (record) => record._id === verificationRecordId
      );

      if (index !== -1) {
        handleEdit(index);
        setEditIndex(index);
      }
    }
  }, [verificationRecordId]);

  const handleUpdateRecord = () => {
    if (editIndex === null) return; // Ensure only updates happen

    const updatedRecord = {
      paymentDate: formik.values.paymentDate || "",
      paymentId: formik.values.paymentId || "",
      amountPaid: parseFloat(formik.values.amountPaid) || 0,
      salesOrderNumber: formik.values.salesOrderNumber || "",
      oldPaymentRemark: formik.values.oldPaymentRemark || "",
      paymentStatus: formik.values.paymentStatus || "Pending",
      remarkDate: formik.values.remarkDate || "",
      remark: formik.values.remark || "",
    };

    let updatedRecords = [...formik.values.verificationRecord];
    const oldRecord = updatedRecords[editIndex]; // Get previous record state

    updatedRecords[editIndex] = updatedRecord;
    formik.setFieldValue("verificationRecord", updatedRecords);

    // ✅ Step 1: Calculate `paymentDetails.amount` (sum of only approved records)
    const approvedRecords = updatedRecords.filter(
      (record) => record.paymentStatus === "Approved"
    );

    const totalAmount = approvedRecords.reduce(
      (sum, record) => sum + (Number(record.amountPaid) || 0),
      0
    );

    // ✅ Step 2: Prepare the updated fields for mutation
    const messages = [];
    const updatedFields = {
      verificationRecord: updatedRecords,
      paymentDetails: {
        ...formik.values.paymentDetails, // Preserve existing values
        amount: totalAmount, // ✅ Update total amountPaid
      },
    };

    // ✅ Step 3: Track field changes in `verificationRecord`
    const trackedFields = [
      "paymentDate",
      "paymentId",
      "amountPaid",
      "salesOrderNumber",
      "oldPaymentRemark",
      "paymentStatus",
      "remarkDate",
      "remark",
    ];

    trackedFields.forEach((field) => {
      const oldValue = oldRecord[field] ?? "N/A";
      const newValue = updatedRecord[field] ?? "N/A";

      if (oldValue !== newValue) {
        messages.push(
          `<b>Payment ID: ${updatedRecord.paymentId}</b>: <b>${field.replace(
            /([A-Z])/g,
            " $1"
          )}</b> changed from <b>${oldValue}</b> to <b>${newValue}</b>`
        );
      }
    });

    const timelineMessage = messages.length > 0 ? messages.join(", ") : "";

    // ✅ Step 4: Update the deal with the new `paymentDetails.amount`
    mutationUpdate.mutate(
      {
        id: dealData?._id,
        dealData: {
          ...updatedFields,
          ...(timelineMessage
            ? { textMessageAboutActivity: timelineMessage }
            : {}),
        },
      },
      {
        onSuccess: (res, { id }) => {
          updateSingleDealCache(id, res.data);

          updateDealsListCache({
            id,
            updatedData: res.data,
            page: urlParams?.page * 1,
            limit: urlParams?.limit * 1,
            queryString: params?.toString(),
            queryParams: queryParams?.toString(),
            companyId: urlParams?.companyid,
          });
          navigate(-1);
        },
      }
    );

    setEditIndex(null);

    // ✅ Step 5: Update `paymentDetails.amount` in Formik state
    formik.setFieldValue("paymentDetails", {
      ...formik.values.paymentDetails,
      amount: totalAmount,
    });

    // ✅ Step 6: Reset only input fields, keep verificationRecord intact
    formik.setFieldValue("paymentDate", "");
    formik.setFieldValue("paymentId", "");
    formik.setFieldValue("amountPaid", "");
    formik.setFieldValue("salesOrderNumber", "");
    formik.setFieldValue("oldPaymentRemark", "");
    formik.setFieldValue("paymentStatus", "Pending");
    formik.setFieldValue("remarkDate", "");
    formik.setFieldValue("remark", "");
  };

  const handleEdit = (index) => {
    const recordToEdit = formik.values.verificationRecord[index];

    if (recordToEdit) {
      formik.setFieldValue("paymentDate", recordToEdit.paymentDate);
      formik.setFieldValue("paymentId", recordToEdit.paymentId);
      formik.setFieldValue("amountPaid", recordToEdit.amountPaid);
      formik.setFieldValue("salesOrderNumber", recordToEdit.salesOrderNumber);
      formik.setFieldValue("oldPaymentRemark", recordToEdit.oldPaymentRemark);
      formik.setFieldValue(
        "paymentStatus",
        recordToEdit.paymentStatus || "Pending"
      );
      formik.setFieldValue("remarkDate", recordToEdit.remarkDate);
      formik.setFieldValue("remark", recordToEdit.remark);
      setEditIndex(index);
    }
  };

  // useEffect(() => {
  //   if (formik.values.verificationRecord?.length > 0) {
  //     const approvedRecords = formik.values.verificationRecord.filter(
  //       (record) => record.paymentStatus === "Approved"
  //     );

  //     const totalAmount = approvedRecords.reduce(
  //       (sum, record) => sum + (Number(record.amountPaid) || 0),
  //       0
  //     );

  //     // Update only if the amount is actually changing
  //     if (formik.values.paymentDetails?.amount !== totalAmount) {
  //       formik.setFieldValue("paymentDetails", {
  //         ...formik.values.paymentDetails,
  //         amount: totalAmount,
  //       });
  //     }
  //   }
  // }, [formik.values.verificationRecord]);

  const columns = [
    {
      field: "action",
      headerName: "Action",
      width: 60,
      renderCell: (params) => {
        const record = formik?.values?.verificationRecord.find(
          (rec) => rec._id === params.row?._id
        );

        const isEditable =
          isEditableByAccountant ||
          (isEditableByCaller && record?.paymentStatus === "Pending");

        return (
          <button
            type="button"
            className="btn btn-sm btn-warning"
            onClick={() => handleEdit(params.id)}
            disabled={!isEditable}
          >
            {isEditable ? (
              <i className="ti ti-pencil" />
            ) : (
              <i className="ti ti-lock" />
            )}
          </button>
        );
      },
    },
    { field: "index", headerName: "S.No", width: 50 },
    {
      field: "paymentDate",
      headerName: "Payment Date",
      width: 150,
      renderCell: (params) => <div className="">{convertToUTCString(params.value,false)}</div>,
    },
    { field: "paymentId", headerName: "Payment ID", width: 150 },
    { field: "salesOrderNumber", headerName: "Sales Order Number", width: 150 },
    {
      field: "oldPaymentRemark",
      headerName: "Old Payment Remarks",
      width: 150,
      renderCell: (params) => (
        <div className="text-capitalize">{params.value}</div>
      ),
    },
    {
      field: "amountPaid",
      headerName: "Amount Paid",
      width: 150,
      renderHeader: () => (
        <span>
          Amount Paid (<MdCurrencyRupee />)
        </span>
      ),
    },
    { field: "paymentStatus", headerName: "Payment Status", width: 150 },
    { field: "remarkDate", headerName: "Remark Date", width: 150 },
    {
      field: "remark",
      headerName: "Remark",
      renderCell: (params) => (
        <div className="text-capitalize">{params.value}</div>
      ),
      width: 150,
    },
  ];

  return (
    <div>
      {formik?.values?.verificationRecord?.length > 0 && (
        <div className="mb-3">
          {" "}
          <div style={{ width: "100%" }}>
            <DataGrid
              rows={formik.values.verificationRecord.map((record, index) => ({
                id: index,
                index: index + 1,
                ...record,
              }))}
              columns={columns}
              autoHeight
              hideFooter
              disableExtendRowFullWidth
            />
          </div>
          {formik?.values?.paymentDetails?.amount > 0 && (
            <div className="row mt-1">
              <div
                className="ms-auto d-flex justify-content-end border-bottom gap-5"
                style={{
                  display: "inline-flex",
                  borderBottom: "2px solid black",
                  width: "fit-content",
                }}
              >
                <span className="fw-bold text-dark">Total Amount Paid:</span>
                <span className="text-capitalize text-dark">
                  {formik?.values?.paymentDetails?.amount ? (
                    <>
                      <MdCurrencyRupee size={15} />
                      {formik?.values?.paymentDetails?.amount}
                    </>
                  ) : (
                    0
                  )}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {formik?.values?.verificationRecord?.length > 0 && (
        <div className="row mb-3">
          {/* Payment Date */}
          <div className="col-md-6">
            <div className="mb-3">
              <label className="col-form-label">Payment Date</label>
              <div className="input-group">
                <input
                  type="date"
                  name="paymentDate"
                  className="form-control"
                  value={`${formik.values.paymentDate}`?.split("T")[0] || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={!isEditableByCaller}
                />
                <div className="input-group-text">
                  {isEditableByCaller ? (
                    <i className="ti ti-pencil" />
                  ) : (
                    <i className="ti ti-lock" />
                  )}
                </div>
              </div>
              {formik.touched.paymentDate && formik.errors.paymentDate ? (
                <div className="text-danger">{formik.errors.paymentDate}</div>
              ) : null}
            </div>
          </div>

          {/* Payment ID */}
          <div className="col-md-6">
            <div className="mb-3">
              <label className="col-form-label">Payment ID</label>
              <div className="input-group">
                <input
                  type="text"
                  name="paymentId"
                  className="form-control"
                  value={formik.values.paymentId || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={!isEditableByCaller}
                />
                <div className="input-group-text">
                  {isEditableByCaller ? (
                    <i className="ti ti-pencil" />
                  ) : (
                    <i className="ti ti-lock" />
                  )}
                </div>
              </div>
              {formik.touched.paymentId && formik.errors.paymentId ? (
                <div className="text-danger">{formik.errors.paymentId}</div>
              ) : null}
            </div>
          </div>

          {/* Sales Order Number */}
          <div className="col-md-6">
            <div className="mb-3">
              <label className="col-form-label">Sales Order Number</label>
              <div className="input-group">
                <input
                  type="text"
                  name="salesOrderNumber"
                  className="form-control"
                  value={formik?.values?.salesOrderNumber}
                  onChange={formik?.handleChange}
                  onBlur={formik?.handleBlur}
                  disabled={!isEditableByCaller}
                />
                <div className="input-group-text">
                  {isEditableByCaller ? (
                    <i className="ti ti-pencil" />
                  ) : (
                    <i className="ti ti-lock" />
                  )}
                </div>
              </div>
              {formik?.touched?.salesOrderNumber &&
              formik?.errors?.salesOrderNumber ? (
                <div className="text-danger">
                  {formik?.errors?.salesOrderNumber}
                </div>
              ) : null}
            </div>
          </div>

          {/* Old Payment Remarks */}
          <div className="col-md-6">
            <div className="mb-3">
              <label className="col-form-label">Old Payment Remarks </label>
              <div className="input-group">
                <input
                  type="text"
                  name="oldPaymentRemark"
                  className="form-control text-capitalize"
                  value={formik?.values?.oldPaymentRemark || "N/A"}
                  onChange={formik?.handleChange}
                  onBlur={formik?.handleBlur}
                  disabled={!isEditableByCaller}
                />
                <div className="input-group-text">
                  {isEditableByCaller ? (
                    <i className="ti ti-pencil" />
                  ) : (
                    <i className="ti ti-lock" />
                  )}
                </div>
              </div>
              {formik?.touched?.oldPaymentRemark &&
              formik?.errors?.oldPaymentRemark ? (
                <div className="text-danger">
                  {formik?.errors?.oldPaymentRemark}
                </div>
              ) : null}
            </div>
          </div>

          {/* Amount Paid */}
          <div className="col-md-6">
            <div className="mb-3">
              <label className="col-form-label">Amount Paid </label>
              <div className="input-group">
                <input
                  type="text"
                  name="amountPaid"
                  className="form-control"
                  value={formik?.values?.amountPaid}
                  onChange={formik?.handleChange}
                  onBlur={formik?.handleBlur}
                  disabled={!isEditableByCaller}
                />
                <div className="input-group-text">
                  {isEditableByCaller ? (
                    <i className="ti ti-pencil" />
                  ) : (
                    <i className="ti ti-lock" />
                  )}
                </div>
              </div>
              {formik?.touched?.amountPaid && formik?.errors?.amountPaid ? (
                <div className="text-danger">{formik?.errors?.amountPaid}</div>
              ) : null}
            </div>
          </div>

          {/* Payment Status */}
          <div className="col-md-6">
            <div className="mb-3">
              <label className="col-form-label">Payment Status</label>
              <div className="input-group">
                <select
                  name="paymentStatus"
                  className="form-select"
                  value={formik.values.paymentStatus ?? ""} // Use nullish coalescing
                  onChange={(e) => {
                    formik.setFieldValue("paymentStatus", e.target.value);
                  }}
                  disabled={!isEditableByAccountant}
                >
                  <option value="">-None-</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                  <option value="Refund">Refund</option>
                  <option value="Loan Cancellation">Loan Cancellation</option>
                  <option value="Loan ForeClosure">Loan ForeClosure</option>
                </select>
                <div className="input-group-text">
                  {isEditableByAccountant ? (
                    <i className="ti ti-pencil" />
                  ) : (
                    <i className="ti ti-lock" />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Remark Date */}
          <div className="col-md-6">
            <div className="mb-3">
              <label className="col-form-label">Remark Date </label>
              <div className="input-group">
                <input
                  type="date"
                  name="remarkDate"
                  className="form-control"
                  value={formik?.values?.remarkDate}
                  onChange={formik?.handleChange}
                  onBlur={formik?.handleBlur}
                  disabled={!isEditableByAccountant}
                />
                <div className="input-group-text">
                  {isEditableByAccountant ? (
                    <i className="ti ti-pencil" />
                  ) : (
                    <i className="ti ti-lock" />
                  )}
                </div>
              </div>
              {formik?.touched?.remarkDate && formik?.errors?.remarkDate ? (
                <div className="text-danger">{formik?.errors?.remarkDate}</div>
              ) : null}
            </div>
          </div>

          {/* Remark */}
          <div className="col-md-6">
            <div className="mb-3">
              <label className="col-form-label">Remark </label>
              <div className="input-group">
                <input
                  type="text"
                  name="remark"
                  className="form-control text-capitalize"
                  value={formik?.values?.remark}
                  onChange={formik?.handleChange}
                  onBlur={formik?.handleBlur}
                  disabled={!isEditableByAccountant}
                />
                <div className="input-group-text">
                  {isEditableByAccountant ? (
                    <i className="ti ti-pencil" />
                  ) : (
                    <i className="ti ti-lock" />
                  )}
                </div>
              </div>
              {formik?.touched?.remark && formik?.errors?.remark ? (
                <div className="text-danger">{formik?.errors?.remark}</div>
              ) : null}
            </div>
          </div>

          <div className="col-md-12">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleUpdateRecord}
              disabled={editIndex === null || !dealData?.editable}
            >
              {editIndex === null ? (
                <>
                  <i className="ti ti-lock me-1" /> Save
                </>
              ) : mutationUpdate?.isPending ? (
                "Saving"
              ) : !dealData?.editable ? (
                <>
                  <span className="me-2">Save</span>{" "}
                  <i className="ti ti-lock fs-5" />
                </>
              ) : (
                "Save"
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditDealVerificationRecord;
